import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { supabase } from '../services/supabaseClient'
import Navbar from '../components/Navbar'

export default function AcceptInvitationPage() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [invitation, setInvitation] = useState<any>(null)
  const [processing, setProcessing] = useState(false)

  useEffect(() => {
    // Store token in session storage immediately
    if (token) {
      sessionStorage.setItem('pendingInvitation', token)
    }
    
    // Check if we're coming from a magic link authentication OR if user is already logged in
    const checkAuthAndValidate = async () => {
      // First check if user is already authenticated
      const { data: { session: currentSession } } = await supabase.auth.getSession()
      if (currentSession?.user) {
        console.log('User already authenticated, checking if we should auto-accept')
        
        // Validate invitation first to get the email
        const inviteData = await validateInvitationOnly()
        
        if (inviteData && inviteData.email === currentSession.user.email) {
          console.log('Authenticated user matches invitation email, auto-accepting')
          validateInvitation(true) // Auto-accept since user is authenticated and email matches
          return
        }
      }
      
      // Check for hash params from magic link
      const hashParams = new URLSearchParams(window.location.hash.substring(1))
      const accessToken = hashParams.get('access_token')
      
      if (accessToken) {
        // Wait for Supabase to process the magic link
        console.log('Magic link detected, waiting for auth to complete...')
        await new Promise(resolve => setTimeout(resolve, 1500))
        
        // Clear the hash to clean up the URL
        window.location.hash = ''
        
        // Check if we have a user session now
        const { data: { session } } = await supabase.auth.getSession()
        if (session?.user) {
          console.log('User authenticated via magic link, auto-accepting invitation')
          
          // Clear any account creation metadata to prevent future account creation
          await supabase.auth.updateUser({
            data: {
              invitation_token: null,
              type: null,
              skip_account_creation: null
            }
          })
          
          validateInvitation(true) // Pass flag to auto-accept
          return
        }
      }
      
      validateInvitation()
    }
    
    checkAuthAndValidate()
  }, [token])

  const validateInvitationOnly = async () => {
    if (!token) return null
    
    try {
      const { data } = await supabase
        .from('invitations')
        .select('*')
        .eq('token', token)
        .maybeSingle()
      
      return data
    } catch (err) {
      console.error('Error fetching invitation:', err)
      return null
    }
  }
  
  const validateInvitation = async (autoAccept = false) => {
    if (!token) {
      setError('Invalid invitation link')
      setLoading(false)
      return
    }
    
    console.log('Validating invitation with token:', token)

    try {
      // Fetch invitation details by token only
      const { data, error: fetchError } = await supabase
        .from('invitations')
        .select(`
          *,
          accounts (name),
          roles (name)
        `)
        .eq('token', token)
        .maybeSingle()  // Use maybeSingle to handle no results gracefully

      if (fetchError) {
        console.error('Error fetching invitation:', fetchError)
        setError('Failed to load invitation. Please try again.')
        setLoading(false)
        return
      }
      
      if (!data) {
        setError('This invitation link is invalid. Please check the URL and try again.')
        setLoading(false)
        return
      }

      // Check if invitation is still valid
      console.log('Invitation data:', { 
        id: data.id, 
        status: data.status, 
        email: data.email,
        expires_at: data.expires_at,
        org_ids: data.org_ids,
        account_id: data.account_id,
        role_id: data.role_id
      })
      
      if (data.status !== 'pending') {
        setError(`This invitation has already been ${data.status}`)
        setLoading(false)
        return
      }

      // Check if expired
      if (new Date(data.expires_at) < new Date()) {
        setError('This invitation has expired')
        // Update status in database
        await supabase
          .from('invitations')
          .update({ status: 'expired' })
          .eq('id', data.id)
        setLoading(false)
        return
      }

      setInvitation(data)
      setLoading(false)
      
      // If auto-accept flag is set, automatically accept the invitation
      if (autoAccept) {
        setTimeout(() => acceptInvitation(), 500)
      }
    } catch (err) {
      console.error('Error validating invitation:', err)
      setError('Something went wrong. Please try again.')
      setLoading(false)
    }
  }

  const acceptInvitation = async () => {
    if (!invitation) return

    setProcessing(true)
    setError(null)

    try {
      // Check if user is logged in
      const { data: { user } } = await supabase.auth.getUser()

      if (!user) {
        // Store invitation token and email
        sessionStorage.setItem('pendingInvitation', token!)
        sessionStorage.setItem('invitationEmail', invitation.email)
        
        // For invited users, send a magic link to bypass beta restrictions
        setProcessing(true)
        setError(null)
        
        try {
          const { error } = await supabase.auth.signInWithOtp({
            email: invitation.email,
            options: {
              emailRedirectTo: `${window.location.origin}/invitation/${token}`,
              data: {
                invitation_token: token,
                skip_account_creation: true
              }
            }
          })
          
          if (error) throw error
          
          // Show success message and redirect
          alert(`We've sent a login link to ${invitation.email}. Click the link in your email to complete your account setup and accept this invitation.`)
          navigate('/login?message=' + encodeURIComponent('Check your email for the login link'))
        } catch (err: any) {
          console.error('Error sending magic link:', err)
          // Fall back to regular login redirect
          navigate(`/login?message=${encodeURIComponent('Please sign in or create an account to accept this invitation')}&email=${encodeURIComponent(invitation.email)}`)
        }
        return
      }

      // Check if email matches
      if (user.email !== invitation.email) {
        setError(`This invitation is for ${invitation.email}. Please sign in with that email address.`)
        setProcessing(false)
        return
      }

      // Check if user already has access to this account
      const { data: existingRole } = await supabase
        .from('user_account_roles')
        .select('id')
        .eq('user_id', user.id)
        .eq('account_id', invitation.account_id)
        .single()

      if (existingRole) {
        setError('You already have access to this account')
        setProcessing(false)
        return
      }

      // Add user to account with the specified role
      console.log('Adding user to account:', {
        user_id: user.id,
        account_id: invitation.account_id,
        role_id: invitation.role_id
      })
      
      const { error: roleError } = await supabase
        .from('user_account_roles')
        .insert({
          user_id: user.id,
          account_id: invitation.account_id,
          role_id: invitation.role_id
        })

      if (roleError) {
        console.error('Error adding user to account:', roleError)
        throw roleError
      }
      
      console.log('User added to account successfully')
      
      // Grant org access if specified in invitation
      console.log('Invitation org_ids:', invitation.org_ids)
      if (invitation.org_ids && invitation.org_ids.length > 0) {
        console.log('Granting access to', invitation.org_ids.length, 'orgs')
        const orgAccessRecords = invitation.org_ids.map(orgId => ({
          user_id: user.id,
          org_id: orgId,
          granted_by: invitation.invited_by,
          granted_at: new Date().toISOString()
        }))
        
        const { error: orgAccessError } = await supabase
          .from('user_org_access')
          .insert(orgAccessRecords)  // Use insert instead of upsert
        
        if (orgAccessError) {
          console.error('Error granting org access:', orgAccessError)
          console.error('Details:', { user_id: user.id, org_ids: invitation.org_ids })
          // Don't throw - user is already added to account
        } else {
          console.log('Org access granted successfully')
        }
      } else {
        console.log('No org_ids in invitation - skipping org access grant')
      }

      // Update invitation status
      console.log('Updating invitation status to accepted')
      const { error: updateError } = await supabase
        .from('invitations')
        .update({ 
          status: 'accepted',
          accepted_at: new Date().toISOString(),
          accepted_by: user.id
        })
        .eq('id', invitation.id)

      if (updateError) {
        console.error('Error updating invitation:', updateError)
      } else {
        console.log('Invitation status updated successfully')
      }

      // Clear the pending invitation from session storage
      sessionStorage.removeItem('pendingInvitation')
      
      // Clear any remaining metadata to ensure clean state
      await supabase.auth.updateUser({
        data: {
          invitation_token: null,
          type: null,
          skip_account_creation: null,
          account_id: null,
          account_name: null,
          role: null,
          invited_by: null
        }
      })
      
      // Show success message briefly
      setError(null)
      const accountName = invitation.accounts?.name || invitation.account_name || 'the account'
      const roleName = invitation.roles?.name || 'a team member'
      alert(`Welcome to ${accountName}! You've been added as ${roleName}.`)
      
      // Redirect to dashboard
      navigate('/')
    } catch (err: any) {
      console.error('Error accepting invitation:', err)
      setError(err.message || 'Failed to accept invitation')
      setProcessing(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar isPublic={true} />
        <div className="flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Validating invitation...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar isPublic={true} />
        <div className="flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md w-full space-y-8">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="mt-6 text-3xl font-extrabold text-gray-900">Invalid Invitation</h2>
              <p className="mt-2 text-sm text-gray-600">{error}</p>
              <div className="mt-6">
                <Link
                  to="/login"
                  className="font-medium text-blue-600 hover:text-blue-500"
                >
                  Go to login
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar isPublic={true} />
      <div className="flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
              You're invited to FlowDocs!
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              You have been invited to join {invitation.accounts?.name}
            </p>
          </div>

          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <dl className="space-y-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Account</dt>
                  <dd className="mt-1 text-sm text-gray-900">{invitation.accounts?.name}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Your Role</dt>
                  <dd className="mt-1 text-sm text-gray-900 capitalize">{invitation.roles?.name}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Invited By</dt>
                  <dd className="mt-1 text-sm text-gray-900">Team Admin</dd>
                </div>
                {invitation.custom_message && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Message</dt>
                    <dd className="mt-1 text-sm text-gray-900">{invitation.custom_message}</dd>
                  </div>
                )}
                <div>
                  <dt className="text-sm font-medium text-gray-500">Expires</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {new Date(invitation.expires_at).toLocaleDateString()}
                  </dd>
                </div>
              </dl>

              <div className="mt-6">
                <button
                  onClick={acceptInvitation}
                  disabled={processing}
                  className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                    processing 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-blue-600 hover:bg-blue-700'
                  } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
                >
                  {processing ? 'Processing...' : 'Accept Invitation'}
                </button>
              </div>
            </div>
          </div>

          <div className="text-center text-sm">
            <span className="text-gray-600">Wrong email? </span>
            <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500">
              Sign in with a different account
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}